/**
 * Read-only live bot statistics — public page, no wallet required.
 * Loads bot state from Parse Cloud (getBotRuntimeState) + on-chain balances.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Link,
  Alert,
  AlertIcon,
  Spinner,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { ethers } from "ethers";
import {
  getChainConfig,
  getRpcUrl,
  getStablecoinAddress,
  getWrappedNativeToken,
} from "../../config/chainsAndDexes";
import {
  fetchBotRuntimeStateFromCloud,
  loadLocalPnlData,
} from "./botMoralisHelpers";
import { DashboardRoot, GlobalStyles, Panel } from "./SniperBotDashboardUI";

const DEFAULT_CHAIN = "arbitrum";
const REFRESH_MS = 20000;

const DEFAULT_BOT_WALLET =
  process.env.REACT_APP_BOT_STATS_WALLET || "0x7081d3fb56f71868ad55780582bc37bbc536e6d6";

const fmt = (n, d = 2) =>
  typeof n === "number" && Number.isFinite(n) ? n.toFixed(d) : "—";

const fmtUsd = (n) => {
  if (!(typeof n === "number") || !Number.isFinite(n)) return "—";
  const sign = n >= 0 ? "" : "-";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

function resolveBotWallet(searchParams) {
  const fromQuery = searchParams.get("wallet")?.trim();
  const raw = fromQuery || DEFAULT_BOT_WALLET;
  try {
    return ethers.utils.getAddress(raw);
  } catch {
    return null;
  }
}

function computeUnrealizedUsd(pos, wethPrice) {
  const holdings = Number(pos?.holdings) || 0;
  const avgEntry = Number(pos?.avgEntry) || 0;
  if (holdings <= 0 || !(wethPrice > 0) || !(avgEntry > 0)) return 0;
  return (wethPrice - avgEntry) * holdings;
}

async function fetchWethUsdPrice() {
  try {
    const res = await fetch(
      "https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd"
    );
    const data = await res.json();
    return Number(data?.ethereum?.usd) || 0;
  } catch {
    return 0;
  }
}

async function fetchOnChainBalances(account, chainId = "arbitrum") {
  const chain = getChainConfig(chainId);
  if (!chain || !account) return null;
  const rpc = getRpcUrl(chainId, "uniswap");
  const provider = new ethers.providers.JsonRpcProvider(rpc);
  const weth = getWrappedNativeToken(chainId, "uniswap");
  const usdc = getStablecoinAddress(chainId, "uniswap");
  const erc20Abi = ["function balanceOf(address) view returns (uint256)"];
  const wethC = new ethers.Contract(weth, erc20Abi, provider);
  const usdcC = new ethers.Contract(usdc, erc20Abi, provider);
  const [nativeBal, wethBal, usdcBal] = await Promise.all([
    provider.getBalance(account),
    wethC.balanceOf(account),
    usdcC.balanceOf(account),
  ]);
  return {
    nativeEth: Number(ethers.utils.formatEther(nativeBal)),
    weth: Number(ethers.utils.formatUnits(wethBal, 18)),
    usdc: Number(ethers.utils.formatUnits(usdcBal, 6)),
  };
}

export default function BotLiveStats() {
  const [searchParams] = useSearchParams();
  const botWallet = useMemo(() => resolveBotWallet(searchParams), [searchParams]);

  const chainConfig = useMemo(() => getChainConfig(DEFAULT_CHAIN), []);
  const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
  const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
  const marketKey = `${wrappedSymbol}-${stableSymbol}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cloudOk, setCloudOk] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [wethPrice, setWethPrice] = useState(0);
  const [chainBalances, setChainBalances] = useState(null);
  const [positions, setPositions] = useState({});
  const [tradeHistory, setTradeHistory] = useState([]);
  const [botEnabled, setBotEnabled] = useState(true);

  const refresh = useCallback(async () => {
    if (!botWallet) {
      setError("Invalid bot wallet. Set REACT_APP_BOT_STATS_WALLET or use ?wallet=0x…");
      setLoading(false);
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const player = String(botWallet);
      const [cloud, price, balances] = await Promise.all([
        fetchBotRuntimeStateFromCloud(player).catch((e) => {
          console.warn("[BotLiveStats] cloud fetch failed:", e);
          return null;
        }),
        fetchWethUsdPrice(),
        fetchOnChainBalances(player, DEFAULT_CHAIN).catch(() => null),
      ]);

      if (cloud) {
        setPositions(cloud.positions || {});
        setTradeHistory(cloud.history || []);
        setBotEnabled(cloud.control?.enabled !== false);
        setCloudOk(true);
      } else {
        setCloudOk(false);
        setError(
          "Could not load bot state from cloud. Deploy Parse cloud code or check REACT_APP_SERVER_URL."
        );
      }

      setWethPrice(price);
      setChainBalances(balances);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [botWallet]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const pos = positions[marketKey] || {};
  const wethHoldings = Number(pos.holdings ?? chainBalances?.weth ?? 0);
  const baseUsdc = Number(chainBalances?.usdc ?? 0);
  const nativeEth = Number(chainBalances?.nativeEth ?? 0);
  const wethUsd = wethHoldings * (wethPrice || 0);
  const nativeUsd = nativeEth * (wethPrice || 0);
  const totalBook = wethUsd + baseUsdc;
  const wethPct = totalBook > 0 ? (wethUsd / totalBook) * 100 : 0;

  const unrealized = computeUnrealizedUsd(pos, wethPrice);
  const costBasis = (pos.avgEntry > 0 ? pos.avgEntry * wethHoldings : 0);
  const drawdownPct =
    pos.avgEntry > 0 && wethPrice > 0
      ? ((wethPrice - pos.avgEntry) / pos.avgEntry) * 100
      : null;

  const distToBuy =
    pos.priceTargetBuy > 0 && wethPrice > 0
      ? ((wethPrice - pos.priceTargetBuy) / pos.priceTargetBuy) * 100
      : null;
  const distToSell =
    pos.priceTargetSell > 0 && wethPrice > 0
      ? ((pos.priceTargetSell - wethPrice) / wethPrice) * 100
      : null;

  const localPnl = loadLocalPnlData();
  const localMarketPnl = localPnl[marketKey];

  return (
    <DashboardRoot>
      <GlobalStyles />
      <VStack align="stretch" spacing={6} maxW="1200px" mx="auto" w="100%">
        <HStack justify="space-between" flexWrap="wrap" gap={3}>
          <VStack align="start" spacing={1}>
            <Heading size="lg" color="cyan.300">
              Bot live statistics
            </Heading>
            <Text fontSize="sm" color="gray.400">
              Read-only · {chainConfig?.name || "Arbitrum"} · cloud + on-chain
            </Text>
          </VStack>
          <Button
            size="sm"
            variant="outline"
            colorScheme="cyan"
            onClick={refresh}
            isLoading={loading}
            isDisabled={!botWallet}
          >
            Refresh
          </Button>
        </HStack>

        {!botWallet && (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            Invalid wallet. Use <Text as="code" mx={1}>?wallet=0x…</Text> or set{" "}
            <Text as="code">REACT_APP_BOT_STATS_WALLET</Text>.
          </Alert>
        )}

        {error && (
          <Alert status={cloudOk ? "warning" : "error"} borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading && !lastRefresh ? (
          <HStack justify="center" py={12}>
            <Spinner color="cyan.300" />
            <Text>Loading bot state…</Text>
          </HStack>
        ) : botWallet ? (
          <>
            <HStack fontSize="xs" color="gray.500" flexWrap="wrap" spacing={2}>
              <Badge colorScheme={cloudOk ? "green" : "red"}>
                {cloudOk ? "Cloud connected" : "Cloud offline"}
              </Badge>
              <Text fontFamily="mono">
                {botWallet.slice(0, 6)}…{botWallet.slice(-4)}
              </Text>
              <Text>·</Text>
              <Text>Updated {lastRefresh ? lastRefresh.toLocaleString() : "—"}</Text>
              <Text>·</Text>
              <Text>ETH ${fmt(wethPrice, 2)}</Text>
            </HStack>

            <SimpleGrid columns={[1, 2, 4]} spacing={4}>
              <StatCard
                label="Trading book (WETH+USDC)"
                value={fmtUsd(totalBook)}
                accent="cyan"
              />
              <StatCard
                label="Unrealized PnL (vs avg entry)"
                value={fmtUsd(unrealized)}
                accent={unrealized >= 0 ? "green" : "red"}
              />
              <StatCard
                label="Drawdown vs entry"
                value={drawdownPct != null ? `${fmt(drawdownPct, 2)}%` : "—"}
                accent={drawdownPct >= 0 ? "green" : "red"}
              />
              <StatCard label="Trades (cloud)" value={String(tradeHistory.length)} />
            </SimpleGrid>

            <SimpleGrid columns={[1, 2, 4]} spacing={4}>
              <StatCard label="WETH holdings" value={fmt(wethHoldings, 4)} sub={fmtUsd(wethUsd)} />
              <StatCard label="USDC balance" value={fmtUsd(baseUsdc)} />
              <StatCard
                label="Native ETH (gas)"
                value={fmt(nativeEth, 5)}
                sub={fmtUsd(nativeUsd)}
              />
              <StatCard
                label="Book split"
                value={`${fmt(wethPct, 0)}% / ${fmt(100 - wethPct, 0)}%`}
                sub="WETH / USDC"
              />
            </SimpleGrid>

            <Panel
              title={`${marketKey} grid targets`}
              subtitle="From Moralis cloud · BotPosition"
              accent="cyan"
            >
              {!cloudOk && (
                <Text fontSize="sm" color="gray.500" mb={3}>
                  Cloud data unavailable — targets and avg entry need Parse cloud.
                </Text>
              )}
              {cloudOk && !pos.avgEntry && !pos.priceTargetBuy && (
                <Text fontSize="sm" color="gray.500" mb={3}>
                  No {marketKey} position saved in cloud yet.
                </Text>
              )}
              <SimpleGrid columns={[1, 2, 3]} spacing={3} fontSize="sm">
                <Text>Avg entry: ${fmt(pos.avgEntry, 2)}</Text>
                <Text>Buy target: ${fmt(pos.priceTargetBuy, 2)}</Text>
                <Text>Sell target: ${fmt(pos.priceTargetSell, 2)}</Text>
                <Text>
                  To buy zone:{" "}
                  {distToBuy != null
                    ? distToBuy <= 0
                      ? "in zone ✓"
                      : `${fmt(distToBuy, 2)}% above`
                    : "—"}
                </Text>
                <Text>
                  To sell zone:{" "}
                  {distToSell != null
                    ? distToSell <= 0
                      ? "in zone ✓"
                      : `${fmt(distToSell, 2)}% below target`
                    : "—"}
                </Text>
                <Text>Cost basis: {fmtUsd(costBasis)}</Text>
                <Text>
                  Status:{" "}
                  {botEnabled && pos.enabled !== false ? (
                    <Badge colorScheme="green">enabled</Badge>
                  ) : (
                    <Badge colorScheme="red">disabled</Badge>
                  )}
                </Text>
                <Text>
                  Last trade:{" "}
                  {pos.lastTradeAt
                    ? new Date(pos.lastTradeAt).toLocaleString()
                    : "—"}
                </Text>
              </SimpleGrid>
            </Panel>

            {localMarketPnl?.totalTrades > 0 && (
              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                Local PnL cache on this browser: realized{" "}
                {fmtUsd(localMarketPnl.realizedPnL)} ({localMarketPnl.totalTrades} trades). Public
                page uses cloud + spot for unrealized.
              </Alert>
            )}

            <Panel title="Recent trades" subtitle="TradeHistory from cloud" accent="purple">
              {tradeHistory.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  No trades in cloud history for this wallet.
                </Text>
              ) : (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th color="gray.400">Time</Th>
                        <Th color="gray.400">Side</Th>
                        <Th color="gray.400">Market</Th>
                        <Th color="gray.400">WETH qty</Th>
                        <Th color="gray.400">Tx</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {tradeHistory.slice(0, 30).map((t, i) => (
                        <Tr key={`${t.txHash || i}-${t.ts}`}>
                          <Td whiteSpace="nowrap">
                            {t.ts ? new Date(t.ts).toLocaleString() : "—"}
                          </Td>
                          <Td>
                            <Badge colorScheme={t.side === "BUY" ? "green" : "red"}>
                              {t.side}
                            </Badge>
                          </Td>
                          <Td>{t.marketKey}</Td>
                          <Td fontFamily="mono">{fmt(Number(t.amount), 4)}</Td>
                          <Td>
                            {t.txHash ? (
                              <Link
                                href={`https://arbiscan.io/tx/${t.txHash}`}
                                isExternal
                                color="cyan.300"
                                fontSize="xs"
                              >
                                {t.txHash.slice(0, 10)}…
                              </Link>
                            ) : (
                              "—"
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Panel>
          </>
        ) : null}
      </VStack>
    </DashboardRoot>
  );
}

function StatCard({ label, value, sub, accent }) {
  const color =
    accent === "green"
      ? "green.300"
      : accent === "red"
      ? "red.300"
      : accent === "cyan"
      ? "cyan.200"
      : "white";
  return (
    <Box
      p={4}
      borderWidth={1}
      borderColor="rgba(148, 163, 184, 0.2)"
      borderRadius="xl"
      bg="rgba(12, 18, 32, 0.92)"
    >
      <Text fontSize="xs" color="gray.400" mb={1} textTransform="uppercase" letterSpacing="wider">
        {label}
      </Text>
      <Text fontSize="xl" fontWeight="bold" fontFamily="mono" color={color}>
        {value}
      </Text>
      {sub && (
        <Text fontSize="xs" color="gray.500" mt={0.5}>
          {sub}
        </Text>
      )}
    </Box>
  );
}
